import { formatPhysicalSize, getPatternMetrics } from './config';
import type { GeneratorConfig, PatternMetrics, PowerRoute } from './types';

const imageCache = new Map<string, Promise<HTMLImageElement>>();
type PatternContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface RenderOptions {
  clear?: boolean;
  logoImage?: CanvasImageSource;
}

export interface LedAnnotationLayout {
  coordinateFontSize: number;
  coordinateInset: number;
  resolutionFontSize: number;
  resolutionInset: number;
}

const CENTER_LABEL_LINE_COUNT = 3;
const CENTER_LABEL_WIDTH_RATIO = 0.92;
const LED_CENTER_LABEL_HEIGHT_RATIO = 0.62;
const PROJECTION_CENTER_LABEL_HEIGHT_RATIO = 0.92;

export async function drawPattern(
  context: PatternContext,
  config: GeneratorConfig,
  options: RenderOptions = {},
): Promise<PatternMetrics> {
  const metrics = getPatternMetrics(config);
  const { width, height } = metrics.resolution;

  context.save();
  context.imageSmoothingEnabled = false;
  if (options.clear !== false) context.clearRect(0, 0, width, height);

  if (config.mode === 'projection') drawProjection(context, config, metrics);
  else drawLed(context, config, metrics);

  if (config.label.enabled) drawLabel(context, config, metrics);
  if (config.logo.dataUrl || options.logoImage) {
    await drawLogo(context, config, metrics, options.logoImage);
  }
  if (config.mode === 'led') drawPowerRoutes(context, config, metrics);
  context.restore();
  return metrics;
}

function drawProjection(
  context: PatternContext,
  config: GeneratorConfig,
  metrics: PatternMetrics,
): void {
  const { width, height } = metrics.resolution;
  const settings = config.projection;
  const stepX = (settings.gridStepMm / metrics.physical.widthMm) * width;
  const stepY = (settings.gridStepMm / metrics.physical.heightMm) * height;

  if (settings.backgroundMode === 'solid') {
    fill(context, settings.backgroundColorA, width, height);
  } else if (settings.backgroundMode === 'checkerboard') {
    drawCheckerboard(
      context,
      width,
      height,
      Math.max(stepX, 1),
      Math.max(stepY, 1),
      settings.backgroundColorA,
      settings.backgroundColorB,
    );
  }

  drawGrid(context, width, height, stepX, stepY, settings.gridLineWidth, settings.gridColor);
  drawBorder(context, width, height, settings.gridLineWidth, settings.gridColor);

  context.strokeStyle = settings.overlayColor;
  context.fillStyle = settings.overlayColor;
  context.lineWidth = settings.overlayLineWidth;
  context.lineCap = 'square';

  if (settings.showDiagonals) {
    context.beginPath();
    context.moveTo(0, 0);
    context.lineTo(width, height);
    context.moveTo(width, 0);
    context.lineTo(0, height);
    context.stroke();
  }
  if (settings.showCenter) {
    const lineWidth = Math.max(1, Math.round(settings.overlayLineWidth));
    context.fillRect(Math.round(width / 2 - lineWidth / 2), 0, lineWidth, height);
    context.fillRect(0, Math.round(height / 2 - lineWidth / 2), width, lineWidth);
  }
  if (settings.showCircle) {
    context.beginPath();
    context.arc(width / 2, height / 2, Math.max(1, Math.min(width, height) * 0.38), 0, Math.PI * 2);
    context.stroke();
  }
}

function drawLed(context: PatternContext, config: GeneratorConfig, metrics: PatternMetrics): void {
  const { width, height } = metrics.resolution;
  const settings = config.led;
  const cabinetWidth = settings.cabinetResolution.width;
  const cabinetHeight = settings.cabinetResolution.height;

  drawCheckerboard(
    context,
    width,
    height,
    cabinetWidth,
    cabinetHeight,
    settings.backgroundColorA,
    settings.backgroundColorB,
  );
  drawGrid(context, width, height, cabinetWidth, cabinetHeight, 2, settings.gridColor);
  drawBorder(context, width, height, 2, settings.gridColor);

  const layout = calculateLedAnnotationLayout(cabinetWidth, cabinetHeight, width, height);
  context.save();
  context.fillStyle = settings.coordinateColor;

  if (settings.showCoordinates) {
    const longestCoordinate = formatCabinetCoordinates(settings.columns - 1, settings.rows - 1);
    const coordinateFontSize = fitFontSize(
      context,
      longestCoordinate,
      Math.min(layout.coordinateFontSize, Math.max(1, cabinetHeight - layout.coordinateInset * 2)),
      Math.max(1, cabinetWidth - layout.coordinateInset * 2),
      1,
    );
    context.font = `600 ${coordinateFontSize}px Arial, sans-serif`;
    context.textAlign = 'left';
    context.textBaseline = 'top';

    for (let y = 0; y < settings.rows; y += 1) {
      for (let x = 0; x < settings.columns; x += 1) {
        context.fillText(
          formatCabinetCoordinates(x, y),
          x * cabinetWidth + layout.coordinateInset,
          y * cabinetHeight + layout.coordinateInset,
        );
      }
    }
  }

  const resolutionText = formatLedResolution(width, height);
  const resolutionFontSize = fitFontSize(
    context,
    resolutionText,
    Math.min(layout.resolutionFontSize, Math.max(1, height - layout.resolutionInset * 2)),
    Math.max(1, width - layout.resolutionInset * 2),
    1,
  );
  context.font = `600 ${resolutionFontSize}px Arial, sans-serif`;
  context.textAlign = 'left';
  context.textBaseline = 'bottom';
  context.fillText(resolutionText, layout.resolutionInset, height - layout.resolutionInset);
  context.restore();
}

export function calculateLedAnnotationLayout(
  cabinetWidth: number,
  cabinetHeight: number,
  screenWidth: number,
  screenHeight: number,
): LedAnnotationLayout {
  const cabinetMinimum = Math.min(cabinetWidth, cabinetHeight);
  const screenMinimum = Math.min(screenWidth, screenHeight);
  const preferredCoordinateFontSize = Math.max(8, Math.min(64, Math.floor(cabinetMinimum * 0.12)));
  const preferredResolutionFontSize = Math.max(12, Math.min(64, Math.floor(screenMinimum * 0.025)));
  return {
    coordinateFontSize: Math.max(
      1,
      Math.min(preferredCoordinateFontSize, Math.floor(cabinetMinimum * 0.2)),
    ),
    coordinateInset: Math.max(1, Math.floor(cabinetMinimum * 0.025)),
    resolutionFontSize: Math.max(
      1,
      Math.min(preferredResolutionFontSize, Math.floor(screenMinimum * 0.12)),
    ),
    resolutionInset: Math.max(1, Math.floor(screenMinimum * 0.008)),
  };
}

export function formatCabinetCoordinates(column: number, row: number): string {
  return `(${column},${row})`;
}

export function formatLedResolution(width: number, height: number): string {
  return `${width}x${height}`;
}

function fitFontSize(
  context: PatternContext,
  text: string,
  preferredSize: number,
  maximumWidth: number,
  minimumSize: number,
): number {
  context.font = `600 ${preferredSize}px Arial, sans-serif`;
  const measuredWidth = context.measureText(text).width;
  if (measuredWidth <= maximumWidth || measuredWidth <= 0) return preferredSize;
  return Math.max(minimumSize, Math.floor(preferredSize * (maximumWidth / measuredWidth)));
}

function drawPowerRoutes(
  context: PatternContext,
  config: GeneratorConfig,
  metrics: PatternMetrics,
): void {
  const cabinetWidth = config.led.cabinetResolution.width;
  const cabinetHeight = config.led.cabinetResolution.height;
  const validPoint = (point: PowerRoute['points'][number]) =>
    Number.isInteger(point.column) &&
    Number.isInteger(point.row) &&
    point.column >= 0 &&
    point.row >= 0 &&
    point.column < config.led.columns &&
    point.row < config.led.rows;

  for (const route of config.power.routes) {
    const cabinetPoints = route.points.filter(validPoint).map((point) => ({
      x: point.column * cabinetWidth + cabinetWidth / 2,
      y: point.row * cabinetHeight + cabinetHeight / 2,
    }));
    if (cabinetPoints.length === 0) continue;

    const renderedPoints = route.incoming
      ? [
          getIncomingEntryPoint(
            cabinetPoints[0]!,
            metrics.resolution.width,
            metrics.resolution.height,
          ),
          ...cabinetPoints,
        ]
      : cabinetPoints;
    drawPowerRoute(context, renderedPoints, route, cabinetWidth, cabinetHeight);
  }
}

function drawPowerRoute(
  context: PatternContext,
  points: { x: number; y: number }[],
  route: PowerRoute,
  cabinetWidth: number,
  cabinetHeight: number,
): void {
  const lineWidth = clamp(route.lineWidth, 2, 80);
  context.save();
  context.lineCap = 'round';
  context.lineJoin = 'round';

  if (points.length > 1) {
    context.beginPath();
    context.moveTo(points[0]!.x, points[0]!.y);
    points.slice(1).forEach((point) => context.lineTo(point.x, point.y));
    context.strokeStyle = 'rgba(5, 8, 12, 0.82)';
    context.lineWidth = lineWidth + Math.max(5, lineWidth * 0.55);
    context.stroke();

    context.beginPath();
    context.moveTo(points[0]!.x, points[0]!.y);
    points.slice(1).forEach((point) => context.lineTo(point.x, point.y));
    context.strokeStyle = route.color;
    context.lineWidth = lineWidth;
    context.stroke();

    for (let index = 0; index < points.length - 1; index += 1) {
      drawDirectionArrow(context, points[index]!, points[index + 1]!, route.color, lineWidth);
    }
  }

  const cabinetPoints = route.incoming ? points.slice(1) : points;
  for (const point of cabinetPoints) {
    context.beginPath();
    context.arc(point.x, point.y, Math.max(7, lineWidth * 0.72), 0, Math.PI * 2);
    context.fillStyle = '#0b0f14';
    context.fill();
    context.lineWidth = Math.max(3, lineWidth * 0.35);
    context.strokeStyle = route.color;
    context.stroke();
  }

  if (route.incoming && route.cableLabel.trim() && points.length > 1) {
    const entry = points[0]!;
    const firstCabinet = points[1]!;
    drawCableLabel(
      context,
      route.cableLabel.trim(),
      {
        x: entry.x + (firstCabinet.x - entry.x) * 0.46,
        y: entry.y + (firstCabinet.y - entry.y) * 0.46,
      },
      route.color,
      Math.max(18, Math.min(64, Math.min(cabinetWidth, cabinetHeight) * 0.14)),
    );
  }
  context.restore();
}

function drawDirectionArrow(
  context: PatternContext,
  from: { x: number; y: number },
  to: { x: number; y: number },
  color: string,
  lineWidth: number,
): void {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  if (length < lineWidth * 4) return;
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const centerX = from.x + (to.x - from.x) * 0.58;
  const centerY = from.y + (to.y - from.y) * 0.58;
  const arrowLength = Math.max(12, lineWidth * 1.35);
  const arrowWidth = arrowLength * 0.72;

  context.save();
  context.translate(centerX, centerY);
  context.rotate(angle);
  context.beginPath();
  context.moveTo(arrowLength * 0.55, 0);
  context.lineTo(-arrowLength * 0.45, -arrowWidth / 2);
  context.lineTo(-arrowLength * 0.18, 0);
  context.lineTo(-arrowLength * 0.45, arrowWidth / 2);
  context.closePath();
  context.fillStyle = '#0b0f14';
  context.fill();
  context.lineWidth = Math.max(2, lineWidth * 0.18);
  context.strokeStyle = color;
  context.stroke();
  context.restore();
}

function drawCableLabel(
  context: PatternContext,
  label: string,
  point: { x: number; y: number },
  color: string,
  fontSize: number,
): void {
  context.save();
  context.font = `700 ${Math.round(fontSize)}px Arial, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const paddingX = fontSize * 0.48;
  const paddingY = fontSize * 0.28;
  const boxWidth = context.measureText(label).width + paddingX * 2;
  const boxHeight = fontSize + paddingY * 2;
  context.fillStyle = 'rgba(5, 8, 12, 0.92)';
  context.fillRect(point.x - boxWidth / 2, point.y - boxHeight / 2, boxWidth, boxHeight);
  context.strokeStyle = color;
  context.lineWidth = Math.max(2, fontSize * 0.08);
  context.strokeRect(point.x - boxWidth / 2, point.y - boxHeight / 2, boxWidth, boxHeight);
  context.fillStyle = '#ffffff';
  context.fillText(label, point.x, point.y + fontSize * 0.02);
  context.restore();
}

export function getIncomingEntryPoint(
  firstCabinet: { x: number; y: number },
  width: number,
  height: number,
): { x: number; y: number } {
  const candidates = [
    { distance: firstCabinet.x, point: { x: 0, y: firstCabinet.y } },
    { distance: width - firstCabinet.x, point: { x: width, y: firstCabinet.y } },
    { distance: firstCabinet.y, point: { x: firstCabinet.x, y: 0 } },
    { distance: height - firstCabinet.y, point: { x: firstCabinet.x, y: height } },
  ];
  candidates.sort((left, right) => left.distance - right.distance);
  return candidates[0]!.point;
}

function drawCheckerboard(
  context: PatternContext,
  width: number,
  height: number,
  cellWidth: number,
  cellHeight: number,
  colorA: string,
  colorB: string,
): void {
  const columns = Math.ceil(width / cellWidth);
  const rows = Math.ceil(height / cellHeight);
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < columns; x += 1) {
      context.fillStyle = (x + y) % 2 === 0 ? colorA : colorB;
      const left = Math.round(x * cellWidth);
      const top = Math.round(y * cellHeight);
      const right = Math.min(width, Math.round((x + 1) * cellWidth));
      const bottom = Math.min(height, Math.round((y + 1) * cellHeight));
      context.fillRect(left, top, right - left, bottom - top);
    }
  }
}

function drawGrid(
  context: PatternContext,
  width: number,
  height: number,
  stepX: number,
  stepY: number,
  lineWidth: number,
  color: string,
): void {
  const size = Math.max(1, Math.round(lineWidth));
  context.fillStyle = color;
  if (Number.isFinite(stepX) && stepX > 0) {
    const effectiveStepX = Math.max(1, stepX);
    for (let index = 0; index * effectiveStepX <= width; index += 1) {
      drawVerticalLine(context, Math.round(index * effectiveStepX), width, height, size);
    }
  }
  if (Number.isFinite(stepY) && stepY > 0) {
    const effectiveStepY = Math.max(1, stepY);
    for (let index = 0; index * effectiveStepY <= height; index += 1) {
      drawHorizontalLine(context, Math.round(index * effectiveStepY), width, height, size);
    }
  }
}

function drawBorder(
  context: PatternContext,
  width: number,
  height: number,
  lineWidth: number,
  color: string,
): void {
  const size = Math.max(1, Math.round(lineWidth));
  context.fillStyle = color;
  context.fillRect(0, 0, width, size);
  context.fillRect(0, height - size, width, size);
  context.fillRect(0, 0, size, height);
  context.fillRect(width - size, 0, size, height);
}

function drawVerticalLine(
  context: PatternContext,
  x: number,
  width: number,
  height: number,
  lineWidth: number,
): void {
  const left = x === 0 ? 0 : x >= width ? width - lineWidth : Math.round(x - lineWidth / 2);
  context.fillRect(left, 0, lineWidth, height);
}

function drawHorizontalLine(
  context: PatternContext,
  y: number,
  width: number,
  height: number,
  lineWidth: number,
): void {
  const top = y === 0 ? 0 : y >= height ? height - lineWidth : Math.round(y - lineWidth / 2);
  context.fillRect(0, top, width, lineWidth);
}

function drawLabel(
  context: PatternContext,
  config: GeneratorConfig,
  metrics: PatternMetrics,
): void {
  const { width, height } = metrics.resolution;
  const preferredFontSize = Math.max(1, Math.round(config.label.fontSize));
  const lines = [
    config.title || 'Untitled screen',
    `${width} x ${height} px`,
    formatPhysicalSize(metrics.physical, config.displayUnit),
  ];
  context.font = `600 ${preferredFontSize}px Arial, sans-serif`;
  const preferredTextWidth = Math.max(...lines.map((line) => context.measureText(line).width));
  const fontSize = calculateCenterLabelFontSize(
    preferredFontSize,
    preferredTextWidth,
    width,
    height,
    config.mode,
  );
  context.font = `600 ${fontSize}px Arial, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  const lineHeight = fontSize * 1.32;
  const textWidth = Math.max(...lines.map((line) => context.measureText(line).width));
  const paddingX = fontSize * 0.7;
  const paddingY = fontSize * 0.5;
  const blockWidth = textWidth + paddingX * 2;
  const blockHeight = lineHeight * lines.length + paddingY * 2;
  const left = width / 2 - blockWidth / 2;
  const top = height / 2 - blockHeight / 2;

  context.save();
  context.globalAlpha = clamp(config.label.backgroundOpacity, 0, 1);
  context.fillStyle = config.label.backgroundColor;
  context.fillRect(left, top, blockWidth, blockHeight);
  context.restore();

  context.fillStyle = config.label.textColor;
  lines.forEach((line, index) => {
    context.fillText(line, width / 2, top + paddingY + lineHeight * (index + 0.5));
  });
}

export function calculateCenterLabelFontSize(
  preferredSize: number,
  measuredTextWidth: number,
  canvasWidth: number,
  canvasHeight: number,
  mode: GeneratorConfig['mode'],
): number {
  const safePreferredSize = Math.max(1, preferredSize);
  const maximumWidth = Math.max(1, canvasWidth * CENTER_LABEL_WIDTH_RATIO);
  const maximumHeight = Math.max(
    1,
    canvasHeight *
      (mode === 'led' ? LED_CENTER_LABEL_HEIGHT_RATIO : PROJECTION_CENTER_LABEL_HEIGHT_RATIO),
  );
  const naturalWidth = Math.max(1, measuredTextWidth + safePreferredSize * 1.4);
  const naturalHeight = safePreferredSize * (CENTER_LABEL_LINE_COUNT * 1.32 + 1);
  const scale = Math.min(1, maximumWidth / naturalWidth, maximumHeight / naturalHeight);
  return Math.max(1, Math.floor(safePreferredSize * scale));
}

async function drawLogo(
  context: PatternContext,
  config: GeneratorConfig,
  metrics: PatternMetrics,
  suppliedImage?: CanvasImageSource,
): Promise<void> {
  if (!suppliedImage && !config.logo.dataUrl) return;
  const image = suppliedImage ?? (await loadImage(config.logo.dataUrl!));
  const dimensions = image as unknown as {
    naturalWidth?: number;
    naturalHeight?: number;
    displayWidth?: number;
    displayHeight?: number;
    videoWidth?: number;
    videoHeight?: number;
    width?: number;
    height?: number;
  };
  const sourceWidth =
    dimensions.naturalWidth ||
    dimensions.displayWidth ||
    dimensions.videoWidth ||
    dimensions.width ||
    0;
  const sourceHeight =
    dimensions.naturalHeight ||
    dimensions.displayHeight ||
    dimensions.videoHeight ||
    dimensions.height ||
    0;
  if (sourceWidth <= 0 || sourceHeight <= 0) return;
  const { width, height } = metrics.resolution;
  const { width: renderedWidth, height: renderedHeight } = calculateLogoRenderSize(
    sourceWidth,
    sourceHeight,
    width,
    height,
    config.logo.scale,
  );
  const margin = Math.max(8, Math.min(width, height) * 0.02);

  context.save();
  context.globalAlpha = clamp(config.logo.opacity, 0, 1);
  context.drawImage(
    image,
    width - renderedWidth - margin,
    height - renderedHeight - margin,
    renderedWidth,
    renderedHeight,
  );
  context.restore();
}

export function calculateLogoRenderSize(
  sourceWidth: number,
  sourceHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  relativeSize: number,
): { width: number; height: number } {
  if (
    sourceWidth <= 0 ||
    sourceHeight <= 0 ||
    canvasWidth <= 0 ||
    canvasHeight <= 0 ||
    ![sourceWidth, sourceHeight, canvasWidth, canvasHeight, relativeSize].every(Number.isFinite)
  ) {
    return { width: 0, height: 0 };
  }
  const size = clamp(relativeSize, 0.02, 0.3);
  const scale = Math.min((canvasWidth * size) / sourceWidth, (canvasHeight * size) / sourceHeight);
  return { width: sourceWidth * scale, height: sourceHeight * scale };
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  const cached = imageCache.get(dataUrl);
  if (cached) return cached;
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The PNG logo could not be loaded.'));
    image.src = dataUrl;
  });
  imageCache.set(dataUrl, promise);
  return promise;
}

function fill(context: PatternContext, color: string, width: number, height: number): void {
  context.fillStyle = color;
  context.fillRect(0, 0, width, height);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
